import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import Quill from 'quill';
import { useTheme } from '@mui/material/styles';
import { Range } from 'quill/core/selection';

import Embed from 'quill/blots/embed';
import { FormFieldProps } from './index';
import { FormControl, FormHelperText, InputLabel, Stack } from '@mui/material';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useFormErrors } from '../../../contexts/FormContext';

import 'quill/dist/quill.core.css';

class SoftLineBreakBlot extends Embed {
    static blotName = 'softbreak';
    static tagName = 'br';
    static className = 'softbreak';
}

Quill.register(SoftLineBreakBlot);

const QuillToolBar = ({ editorId }: { editorId: string }) => {
    return (
        <div id={`${editorId}-toolbar`}>
            <select className="ql-header" defaultValue="">
                <option value=""></option>
                <option value="1"></option>
                <option value="2"></option>
                <option value="3"></option>
                <option value="4"></option>
                <option value="5"></option>
                <option value="6"></option>
            </select>
            <button className="ql-bold"></button>
            <button className="ql-italic"></button>
            <button className="ql-underline"></button>
        </div>
    );
};

interface TextEditorFormFieldProps extends FormFieldProps {
    blockIndex?: number;
}

export default function TextEditorFormField({
    formIndex,
    name,
    label,
    disabled,
    required,
    placeholder,
    value,
    onChange,
    cssClassName,
    blockIndex,
    help,
}: TextEditorFormFieldProps) {
    const quillRef = useRef<Quill>();

    const theme = useTheme();
    const { translate } = useTranslation();
    const { error } = useFormErrors(name);

    const [focus, setFocus] = useState(false);
    const [loaded, setLoaded] = useState(false);

    let id = `form-${formIndex}-texteditor-${name}`;
    if (typeof blockIndex !== 'undefined') {
        id = `block-${blockIndex}-${id}`;
    }

    const setEditorContent = (content: string) => {
        const delta = quillRef.current?.clipboard.convert({ html: content });
        if (delta) {
            quillRef?.current?.setContents(delta, 'silent');
            if (!focus) {
                setLoaded(true);
            }
        }
    };

    const getEditorContent = () => {
        let html = quillRef.current?.getSemanticHTML();
        if (html && html.match(/(?:\<pre[\S\s]*?\>)([\S\s]*?)(?:\<\/pre\>)/)) {
            html = html.replace(
                /(?:\<pre[\S\s]*?\>)([\S\s]*?)(?:\<\/pre\>)/,
                '$1',
            );
            setEditorContent(html);
        }
        return getTextLength() > 1 ? html : undefined;
    };

    const getTextLength = () => {
        const text = quillRef.current?.getText();
        return text?.length ?? 0;
    };

    const textChange = () => {
        onChange(getEditorContent());
    };

    useEffect(() => {
        if (typeof value !== 'undefined' && value !== getEditorContent()) {
            setEditorContent(value);
        }
    }, [value, quillRef]);

    const onFocus = () => {
        setFocus(true);
    };

    const offFocus = () => {
        setFocus(false);
    };

    const initQuill = (quillContainer: HTMLElement) => {
        quillRef.current = new Quill(quillContainer, {
            modules: {
                toolbar: {
                    container: `#${id}-toolbar`,
                },
                keyboard: {
                    bindings: {
                        enter: {
                            key: 'Enter',
                            handler: (range: Range) => {
                                quillRef.current?.insertEmbed(
                                    range.index,
                                    'softbreak',
                                    Quill.sources.USER,
                                );
                                quillRef.current?.setSelection(
                                    range.index + 1,
                                    Quill.sources.SILENT,
                                );
                                return false;
                            },
                        },
                    },
                },
            },
            readOnly: disabled,
            placeholder,
            theme: 'bubble',
        });

        quillRef.current.on('text-change', textChange);
        quillContainer.firstChild?.addEventListener('focus', onFocus);
        quillContainer.firstChild?.addEventListener('blur', offFocus);

        return () => {
            quillContainer.firstChild?.removeEventListener('focus', onFocus);
            quillContainer.firstChild?.removeEventListener('blur', offFocus);

            if (quillRef.current) {
                quillRef.current.off('text-change', textChange);
                quillRef.current.disable();
            }
        };
    };

    useLayoutEffect(() => {
        const quillContainer = document.getElementById(id);
        if (quillContainer) {
            return initQuill(quillContainer);
        }
    }, []);

    return (
        <FormControl fullWidth className={cssClassName}>
            <Stack spacing={0.5}>
                <InputLabel
                    shrink={focus || getTextLength() > 1}
                    sx={{
                        mt: 0.5,
                        color: focus
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                    }}>
                    {translate(label)}
                </InputLabel>
                <QuillToolBar editorId={id} />
                <div
                    id={id}
                    style={{
                        borderWidth: 1,
                        borderColor: focus
                            ? theme.palette.primary.light
                            : theme.palette.action.focus,
                        borderStyle: 'solid',
                        borderRadius: 5,
                        background: 'transparent',
                        overflow: 'visible',
                    }}></div>
                {Boolean(error) && (
                    <FormHelperText error id={name + '-error'}>
                        {translate(error ?? 'error')}
                    </FormHelperText>
                )}
            </Stack>
        </FormControl>
    );
}
